/**
 * Pure recipe entity model — normalize, validate, version, status.
 * No inventory I/O. No persistence.
 *
 * Recipe.portions = BOM base units (SSOT shared by POS + Production):
 * ingredient lines are written for this many units of primary output
 * (menu portions for plats, or stock units of finished goods for prep/FG formulas).
 * See recipeUsageScale.js for sale vs production scaling.
 */

import { sameProductId } from '../../../utils/productIdUtils';

export const RECIPE_STATUS = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived'
});

export const RECIPE_INGREDIENT_KIND = Object.freeze({
  PRODUCT: 'product',
  SUB_RECIPE: 'sub_recipe'
});

/**
 * @param {unknown} status
 * @returns {'draft'|'active'|'archived'}
 */
export function normalizeRecipeStatus(status) {
  const key = String(status || '')
    .trim()
    .toLowerCase();
  if (key === RECIPE_STATUS.ACTIVE) return RECIPE_STATUS.ACTIVE;
  if (key === RECIPE_STATUS.ARCHIVED) return RECIPE_STATUS.ARCHIVED;
  return RECIPE_STATUS.DRAFT;
}

/**
 * @param {unknown} kind
 * @returns {'product'|'sub_recipe'}
 */
export function normalizeIngredientKind(kind) {
  const key = String(kind || '')
    .trim()
    .toLowerCase();
  if (key === RECIPE_INGREDIENT_KIND.SUB_RECIPE || key === 'subrecipe' || key === 'recipe') {
    return RECIPE_INGREDIENT_KIND.SUB_RECIPE;
  }
  return RECIPE_INGREDIENT_KIND.PRODUCT;
}

/**
 * @param {unknown} version
 * @returns {number}
 */
export function normalizeRecipeVersion(version) {
  const n = Number(version);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

/**
 * @param {unknown} value
 * @param {number} [fallback=1]
 * @returns {number}
 */
function positiveNumber(value, fallback = 1) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return n;
}

/**
 * preparationYield as fraction in (0, 1] — 0.75 = 75% usable.
 * Values > 1 treated as percent (75 → 0.75).
 * @param {unknown} raw
 * @returns {number}
 */
export function normalizePreparationYield(raw) {
  if (raw == null || raw === '') return 1;
  let n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 1;
  if (n > 1) n = n / 100;
  if (n > 1) return 1;
  return n;
}

/**
 * Preparation loss as fraction in [0, 1). 0.1 = 10% lost → need net/(1-loss).
 * @param {unknown} raw
 * @returns {number}
 */
export function normalizePreparationLoss(raw) {
  if (raw == null || raw === '') return 0;
  let n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n >= 1 && n <= 100) n = n / 100;
  if (n >= 1) return 0.999;
  return n;
}

/**
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeRecipeIngredient(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const kind = normalizeIngredientKind(raw.kind ?? raw.type);
  const quantity = Number(raw.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;

  const unit = String(raw.unit || '').trim().toLowerCase() || null;
  const conversionToStock =
    raw.conversionToStock != null && Number.isFinite(Number(raw.conversionToStock))
      ? Number(raw.conversionToStock)
      : raw.conversionFactor != null && Number.isFinite(Number(raw.conversionFactor))
        ? Number(raw.conversionFactor)
        : null;
  const preparationLoss = normalizePreparationLoss(raw.preparationLoss ?? raw.loss);

  if (kind === RECIPE_INGREDIENT_KIND.SUB_RECIPE) {
    const subRecipeId = String(raw.subRecipeId ?? raw.recipeId ?? '').trim();
    if (!subRecipeId) return null;
    return {
      kind,
      subRecipeId,
      quantity,
      unit,
      conversionToStock,
      preparationLoss,
      notes: raw.notes != null ? String(raw.notes) : undefined
    };
  }

  const productId = String(raw.productId ?? '').trim();
  if (!productId) return null;
  return {
    kind: RECIPE_INGREDIENT_KIND.PRODUCT,
    productId,
    quantity,
    unit,
    conversionToStock,
    preparationLoss,
    notes: raw.notes != null ? String(raw.notes) : undefined
  };
}

/**
 * @param {unknown} raw
 * @returns {object[]}
 */
export function normalizeRecipeIngredients(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const entry of raw) {
    const line = normalizeRecipeIngredient(entry);
    if (line) out.push(line);
  }
  return out;
}

/**
 * Normalize a full recipe document. Does not enforce business uniqueness.
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeRecipe(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const dishId = String(raw.dishId ?? raw.productId ?? '').trim();
  if (!id || !dishId) return null;

  const version = normalizeRecipeVersion(raw.version);
  const status = normalizeRecipeStatus(raw.status);
  const portions = positiveNumber(raw.portions, 1);
  const preparationYield = normalizePreparationYield(raw.preparationYield ?? raw.yield);
  const preparationTimeMinutes =
    raw.preparationTimeMinutes != null
      ? Number(raw.preparationTimeMinutes)
      : raw.preparationTime != null
        ? Number(raw.preparationTime)
        : null;
  const name = String(raw.name || '').trim() || `Recipe ${dishId} v${version}`;
  const ingredients = normalizeRecipeIngredients(raw.ingredients);

  const preparationCostFixed =
    raw.preparationCostFixed != null && Number.isFinite(Number(raw.preparationCostFixed))
      ? Number(raw.preparationCostFixed)
      : raw.preparationCost != null && Number.isFinite(Number(raw.preparationCost))
        ? Number(raw.preparationCost)
        : 0;

  const sellingPrice =
    raw.sellingPrice != null && Number.isFinite(Number(raw.sellingPrice))
      ? Number(raw.sellingPrice)
      : null;

  return {
    id,
    dishId,
    name,
    version,
    status,
    portions,
    preparationYield,
    preparationTimeMinutes:
      Number.isFinite(preparationTimeMinutes) && preparationTimeMinutes >= 0
        ? preparationTimeMinutes
        : null,
    preparationCostFixed,
    sellingPrice,
    ingredients,
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
    supersededByRecipeId:
      raw.supersededByRecipeId != null && String(raw.supersededByRecipeId).trim()
        ? String(raw.supersededByRecipeId).trim()
        : null,
    migratedFromIncludedStockItems: raw.migratedFromIncludedStockItems === true
  };
}

/**
 * @param {unknown} recipe
 * @returns {{ ok: true, recipe: object } | { ok: false, errors: string[] }}
 */
export function validateRecipe(recipe) {
  const errors = [];
  const normalized = normalizeRecipe(recipe);
  if (!normalized) {
    return { ok: false, errors: ['Recipe requires id and dishId'] };
  }
  if (!normalized.ingredients.length) {
    errors.push('Recipe requires at least one ingredient');
  }
  if (!(normalized.portions > 0)) {
    errors.push('Recipe portions must be > 0');
  }
  if (!(normalized.preparationYield > 0 && normalized.preparationYield <= 1)) {
    errors.push('preparationYield must be in (0, 1]');
  }
  for (let i = 0; i < normalized.ingredients.length; i += 1) {
    const line = normalized.ingredients[i];
    if (line.kind === RECIPE_INGREDIENT_KIND.PRODUCT && !line.productId) {
      errors.push(`ingredients[${i}]: productId required`);
    }
    if (line.kind === RECIPE_INGREDIENT_KIND.SUB_RECIPE && !line.subRecipeId) {
      errors.push(`ingredients[${i}]: subRecipeId required`);
    }
  }
  if (errors.length) return { ok: false, errors };
  return { ok: true, recipe: normalized };
}

export function isActiveRecipe(recipe) {
  return normalizeRecipeStatus(recipe?.status) === RECIPE_STATUS.ACTIVE;
}

export function isDraftRecipe(recipe) {
  return normalizeRecipeStatus(recipe?.status) === RECIPE_STATUS.DRAFT;
}

export function isArchivedRecipe(recipe) {
  return normalizeRecipeStatus(recipe?.status) === RECIPE_STATUS.ARCHIVED;
}

/**
 * @param {unknown} a
 * @param {unknown} b
 * @returns {number} negative if a < b
 */
export function compareRecipeVersions(a, b) {
  return normalizeRecipeVersion(a) - normalizeRecipeVersion(b);
}

/**
 * Ensure at most one active recipe per dishId (highest version wins if multiple).
 * All others for that dish become archived when one is activated.
 * Pure: returns new array.
 *
 * @param {unknown[]} recipes
 * @param {string} recipeId
 * @returns {object[]}
 */
export function activateRecipeInList(recipes, recipeId) {
  const list = (Array.isArray(recipes) ? recipes : [])
    .map((r) => normalizeRecipe(r))
    .filter(Boolean);
  const targetId = String(recipeId || '').trim();
  const target = list.find((r) => r.id === targetId);
  if (!target) return list;

  return list.map((r) => {
    if (r.id === targetId) {
      return { ...r, status: RECIPE_STATUS.ACTIVE, updatedAt: new Date().toISOString() };
    }
    if (sameProductId(r.dishId, target.dishId) && r.status === RECIPE_STATUS.ACTIVE) {
      return {
        ...r,
        status: RECIPE_STATUS.ARCHIVED,
        supersededByRecipeId: targetId,
        updatedAt: new Date().toISOString()
      };
    }
    return r;
  });
}

/**
 * @param {unknown[]} recipes
 * @param {string} recipeId
 * @returns {object[]}
 */
export function archiveRecipeInList(recipes, recipeId) {
  const targetId = String(recipeId || '').trim();
  return (Array.isArray(recipes) ? recipes : [])
    .map((r) => normalizeRecipe(r))
    .filter(Boolean)
    .map((r) =>
      r.id === targetId
        ? { ...r, status: RECIPE_STATUS.ARCHIVED, updatedAt: new Date().toISOString() }
        : r
    );
}

/**
 * Next version number for a dish among existing recipes.
 * @param {unknown[]} recipes
 * @param {string} dishId
 * @returns {number}
 */
export function nextRecipeVersion(recipes, dishId) {
  const id = String(dishId || '').trim();
  let max = 0;
  for (const raw of Array.isArray(recipes) ? recipes : []) {
    const r = normalizeRecipe(raw);
    if (!r || !sameProductId(r.dishId, id)) continue;
    if (r.version > max) max = r.version;
  }
  return max + 1;
}
